export type DodGlossaryTermId =
  | 'cmmc'
  | 'fci'
  | 'cui'
  | 'c3pao'
  | 'sprs'
  | 'dfars'
  | 'daffars'
  | 'far'
  | 'poam'
  | 'ssp'
  | 'nist'
  | 'gccHigh'
  | 'eo'
  | 'cgl'
  | 'do'
  | 'epli'
  | 'bop'
  | 'wc'
  | 'hnoa'
  | 'millerAct'
  | 'coi'
  | 'acord'
  | 'dba'
  | 'sow'
  | 'llc'
  | 'ein'
  | 'w9'
  | 'w8ben'
  | 'uei'
  | 'cage'
  | 'naics'
  | 'gsa'
  | 'sam'
  | 'piee'
  | 'eft'
  | 'duns'
  | 'spf'
  | 'dkim'
  | 'dmarc'
  | 'bimi'
  | 'zeroTrust'
  | 'cis'
  | 'asvs'
  | 'mfa'
  | 'sso'
  | 'waf'
  | 'cspm'
  | 'sbom'
  | 'cisa'
  | 'ofac'
  | 'sdn'
  | 'capta'
  | 'dmca'
  | 'gdpr'
  | 'ccpa'
  | 'wcag'
  | 'hipaa'
  | 'pii'
  | 'seo'
  | 'coppa'
  | 'ferpa'
  | 'hecvat'
  | 'glba'
  | 'pci'
  | 'canspam'
  | 'gpc'
  | 'cmp'
  | 'hsts'
  | 'csp'
  | 'ada'
  | 'sba'
  | 'apex'
  | 'dsbs'
  | 'hub'
  | 'vethub'
  | 'txRamp'
  | 'fedramp'
  | 'soc2'
  | 'tsc'
  | 'fips'
  | 'taa'
  | 'gsar'
  | 'govramp'
  | 'spectrim'
  | 'hubzone'
  | 'saeps'
  | 'civcast'
  | 'wosb'
  | 'eightA'
  | 'fcl'
  | 'dd254'
  | 'ato'
  | 'hacs'
  | 'oasis'
  | 'nigp'
  | 'dir'
  | 'esbd'
  | 'cmbl'
  | 'sbs'
  | 'vpts'
  | 'franchiseTax'
  | 'buyboard'
  | 'tips'
  | 'hgacBuy'
  | 'bidnet'
  | 'clery'
  | 'titleIx'
  | 'ipeds'
  | 'vawa'
  | 'irb'
  | 'hea'
  | 'sbir'
  | 'sttr'
  | 'dsip'
  | 'aiRmf'
  | 'iso42001'
  | 'omb'
  | 'kpi'
  | 'erisa'
  | 'sns'
  | 'mdm'
  | 'gatekeeper'
  | 'https';

export type DodGlossaryTerm = {
  id: DodGlossaryTermId;
  pattern: RegExp;
  label: { en: string; es: string };
  help: { en: string; es: string };
};

export type DodGlossaryChunk = {
  text: string;
  termId?: DodGlossaryTermId;
};

export const DOD_GLOSSARY: DodGlossaryTerm[] = [
  {
    id: 'eo',
    pattern: /\bE&O\b|errors and omissions insurance|seguro de errores y omisiones/gi,
    label: { en: 'E&O', es: 'E&O' },
    help: {
      en: 'Errors and omissions, also called professional liability insurance. It pays if the company makes a mistake in its advice or services. Not a FAR minimum, but primes and IT notices usually ask for it.',
      es: 'Errores y omisiones, también llamado seguro de responsabilidad profesional. Cubre si la empresa se equivoca en su consejo o servicio. No es un mínimo FAR, pero los primes y los avisos de TI lo piden.',
    },
  },
  {
    id: 'irs',
    pattern: /\bIRS\b/g,
    label: { en: 'IRS', es: 'IRS' },
    help: {
      en: 'Internal Revenue Service: the U.S. tax agency. It issues the company tax ID and receives tax-election forms.',
      es: 'Servicio de Impuestos Internos: la agencia fiscal de EE.UU. Emite el ID fiscal de la empresa y recibe las elecciones de impuestos.',
    },
  },
  {
    id: 'fincen',
    pattern: /\bFinCEN\b/g,
    label: { en: 'FinCEN', es: 'FinCEN' },
    help: {
      en: 'Financial Crimes Enforcement Network: the Treasury bureau that collects beneficial-ownership reports from certain foreign companies registered to do business in the United States.',
      es: 'Red de ejecución de delitos financieros: la oficina del Tesoro que recibe reportes de titularidad real de ciertas empresas extranjeras registradas para operar en Estados Unidos.',
    },
  },
  {
    id: 'boi',
    pattern: /\bBOI\b/g,
    label: { en: 'BOI', es: 'BOI' },
    help: {
      en: 'Beneficial ownership information: who owns or controls the company. The August 2026 FinCEN rule exempts companies formed in the United States. Certain foreign companies registered in a U.S. state still report, and they leave out U.S. person owners.',
      es: 'Información de titularidad real: quién posee o controla la empresa. La regla de FinCEN de agosto de 2026 exime a las empresas formadas en Estados Unidos. Ciertas empresas extranjeras registradas en un estado sí reportan, y omiten a los dueños que son personas de EE.UU.',
    },
  },
  {
    id: 'cgl',
    pattern: /\bCGL\b/g,
    label: { en: 'CGL', es: 'CGL' },
    help: {
      en: 'Commercial general liability: insurance if someone is hurt or property is damaged because of the company’s work. FAR 28.307-2 sets a floor of $500,000 per occurrence; the bid-ready target is $1 million.',
      es: 'Responsabilidad general comercial: seguro si alguien se lastima o hay daño a bienes por el trabajo de la empresa. FAR 28.307-2 fija 500.000 USD por ocurrencia; el objetivo para licitar es 1 millón.',
    },
  },
  {
    id: 'do',
    pattern: /\bD&O\b/g,
    label: { en: 'D&O', es: 'D&O' },
    help: {
      en: 'Directors and officers insurance. It pays if an owner or manager is sued over how the company is run. It is not general liability, and it is not required until a contract or an investor demands it.',
      es: 'Seguro de directores y oficiales. Paga si demandan a un dueño o gerente por cómo se dirige la empresa. No es responsabilidad general, y no es obligatorio hasta que un contrato o un inversionista lo exija.',
    },
  },
  {
    id: 'epli',
    pattern: /\bEPLI\b/g,
    label: { en: 'EPLI', es: 'EPLI' },
    help: {
      en: 'Employment practices liability insurance. It pays claims by workers such as wrongful firing, discrimination, harassment, or retaliation. It does not replace workers’ compensation.',
      es: 'Seguro de responsabilidad por prácticas de empleo. Paga reclamos de trabajadores como despido injusto, discriminación, acoso o represalias. No sustituye la compensación laboral.',
    },
  },
  {
    id: 'bop',
    pattern: /\bBOP\b/g,
    label: { en: 'BOP', es: 'BOP' },
    help: {
      en: 'Business owner’s policy. A package that often combines general liability and coverage for the company’s equipment. Do not buy a second general liability policy if the package already includes it.',
      es: 'Póliza de propietario de negocio. Un paquete que suele combinar responsabilidad general y cobertura del equipo de la empresa. No compre una segunda póliza de responsabilidad general si el paquete ya la incluye.',
    },
  },
  {
    id: 'wc',
    pattern: /\bworkers['’] compensation\b/gi,
    label: { en: 'workers’ compensation', es: 'compensación laboral' },
    help: {
      en: 'Pays medical bills and lost wages if a worker is hurt on the job. FAR 28.307-2 also wants employer’s liability of at least $100,000. Texas does not require it for most private employers, but government contracts often do. Health insurance is not a substitute.',
      es: 'Paga médico y salario perdido si un trabajador se lastima en el trabajo. FAR 28.307-2 también pide responsabilidad del empleador de al menos 100.000 USD. Texas no la exige a la mayoría de empleadores privados, pero los contratos de gobierno a menudo sí. El seguro de salud no la sustituye.',
    },
  },
  {
    id: 'hnoa',
    pattern: /\bHNOA\b/g,
    label: { en: 'HNOA', es: 'HNOA' },
    help: {
      en: 'Hired and non-owned auto: coverage when someone drives a personal or rental car for company work. Personal auto usually will not pay that trip. Buy a full business auto policy only if the company owns a vehicle.',
      es: 'Auto alquilado y de no propietarios: cubre cuando alguien maneja un auto personal o de renta para el trabajo de la empresa. El seguro personal normalmente no paga ese viaje. Compre una póliza comercial completa solo si la empresa tiene un vehículo.',
    },
  },
  {
    id: 'millerAct',
    pattern: /\bMiller Act\b/g,
    label: { en: 'Miller Act', es: 'Miller Act' },
    help: {
      en: 'Federal construction bonding law. Performance and payment bonds are required on most federal construction over $150,000. Ordinary IT consulting is not construction — skip bonds unless the solicitation asks.',
      es: 'Ley federal de fianzas de construcción. Las fianzas de cumplimiento y pago se exigen en la mayoría de la construcción federal de más de 150.000 USD. La consultoría TI ordinaria no es construcción: omita las fianzas salvo que la solicitud las pida.',
    },
  },
  {
    id: 'acord',
    pattern: /\bACORD(?:\s*25)?\b/g,
    label: { en: 'ACORD 25', es: 'ACORD 25' },
    help: {
      en: 'The usual one-page Certificate of Insurance form. It shows named insured, limits, and dates. It is proof of coverage, not the policy itself.',
      es: 'El certificado de seguro de una página más usado. Muestra asegurado nombrado, límites y fechas. Es prueba de cobertura, no la póliza.',
    },
  },
  {
    id: 'coi',
    pattern: /\bCOIs?\b/g,
    label: { en: 'COI', es: 'COI' },
    help: {
      en: 'Certificate of Insurance: a one-page proof that the policy exists. Contracting officers keep it in the contract file (FAR 52.228-5 / FAR 4.803). It does not replace the policy.',
      es: 'Certificado de seguro: prueba de una página de que la póliza existe. El oficial de contratación lo guarda en el expediente (FAR 52.228-5 / FAR 4.803). No sustituye la póliza.',
    },
  },
  {
    id: 'daffars',
    pattern: /\bDAFFARS\b/g,
    label: { en: 'DAFFARS', es: 'DAFFARS' },
    help: {
      en: 'Department of the Air Force Federal Acquisition Regulation Supplement. DAFFARS 5328.310 tells contracting officers to request and keep proof of insurance.',
      es: 'Suplemento de adquisiciones de la Fuerza Aérea. DAFFARS 5328.310 pide al oficial de contratación solicitar y guardar prueba de seguro.',
    },
  },
  {
    id: 'dba',
    pattern: /\bDBA\b/g,
    label: { en: 'DBA', es: 'DBA' },
    help: {
      en: 'Doing business as: a trade name (here, John Forrest & Goss). Insurance and SAM must still use the legal LLC name as named insured / legal business name.',
      es: 'Nombre comercial (aquí, John Forrest & Goss). El seguro y SAM deben usar el nombre legal de la LLC como asegurado nombrado / razón social.',
    },
  },
  {
    id: 'sow',
    pattern: /\bSOWs?\b/g,
    label: { en: 'SOW', es: 'SOW' },
    help: {
      en: 'Statement of Work: the document that says what the contractor will do on a job. Bind insurance before the first SOW.',
      es: 'Declaración de trabajo: el documento que dice qué hará el contratista. Contrate el seguro antes del primer SOW.',
    },
  },
  {
    id: 'piee',
    pattern: /\bPIEE\b/g,
    label: { en: 'PIEE', es: 'PIEE' },
    help: {
      en: 'Procurement Integrated Enterprise Environment: the DoD portal used after you have a CAGE, including SPRS.',
      es: 'Entorno empresarial integrado de adquisiciones: el portal del DoD que se usa después del CAGE, incluido SPRS.',
    },
  },
  {
    id: 'psc',
    pattern: /\bPSC\b/g,
    label: { en: 'PSC', es: 'PSC' },
    help: {
      en: 'Product or Service Code: a four-character code for what the government is buying on a contract line. NAICS says what kind of company it is. PSC says what that contract line bought. A buyer reports the main PSC to FPDS. Copy only codes that match work this company performs.',
      es: 'Código de producto o servicio: cuatro caracteres de lo que el gobierno compra en una línea del contrato. NAICS dice qué tipo de empresa es. PSC dice lo que compró esa línea. El comprador reporta el PSC principal a FPDS. Copie solo códigos que correspondan al trabajo de esta empresa.',
    },
  },
  {
    id: 'fpds',
    pattern: /\bFPDS\b/g,
    label: { en: 'FPDS', es: 'FPDS' },
    help: {
      en: 'Federal Procurement Data System: the public record of federal contract actions. Each action stores the main PSC for what was bought.',
      es: 'Sistema federal de datos de adquisiciones: el registro público de acciones de contratos federales. Cada acción guarda el PSC principal de lo que se compró.',
    },
  },
  {
    id: 'naics',
    pattern: /\bNAICS\b/g,
    label: { en: 'NAICS', es: 'NAICS' },
    help: {
      en: 'North American Industry Classification System: the codes that say what kind of work the company does. Primary for this IT consulting path is 541512.',
      es: 'Sistema de clasificación industrial de América del Norte: códigos del tipo de trabajo. El principal de esta ruta de consultoría TI es 541512.',
    },
  },
  {
    id: 'cage',
    pattern: /\bCAGE\b/g,
    label: { en: 'CAGE', es: 'CAGE' },
    help: {
      en: 'Commercial and Government Entity code: a five-character ID DoD uses after SAM is Active. Do not invent one.',
      es: 'Código de entidad comercial y gubernamental: un ID de cinco caracteres que el DoD asigna cuando SAM está Active. No lo invente.',
    },
  },
  {
    id: 'uei',
    pattern: /\bUEI\b/g,
    label: { en: 'UEI', es: 'UEI' },
    help: {
      en: 'Unique Entity ID: the 12-character ID SAM.gov assigns. It replaced DUNS. Do not buy a DUNS and do not type a guessed UEI.',
      es: 'Identificador único de entidad: el ID de 12 caracteres que asigna SAM.gov. Sustituyó al DUNS. No compre un DUNS ni invente el UEI.',
    },
  },
  {
    id: 'form990',
    pattern: /\bForm 990\b/g,
    label: { en: 'Form 990', es: 'Form 990' },
    help: {
      en: 'The annual information return most tax-exempt organizations file with the IRS. Smaller organizations may use the short form or the e-postcard. It is not a bill.',
      es: 'La declaración anual de información que la mayoría de las organizaciones exentas presentan al IRS. Las más pequeñas pueden usar el formulario corto o la postal electrónica. No es una factura.',
    },
  },
  {
    id: 'ein',
    pattern: /\bEIN\b/g,
    label: { en: 'EIN', es: 'EIN' },
    help: {
      en: 'Employer Identification Number: the IRS tax ID for the company. It is on the CP575 letter. Do not invent it.',
      es: 'Número de identificación del empleador: el ID fiscal del IRS. Está en la carta CP575. No lo invente.',
    },
  },
  {
    id: 'w9',
    pattern: /\bW-9s?\b/g,
    label: { en: 'W-9', es: 'W-9' },
    help: {
      en: 'IRS form a U.S. contractor fills out before you pay them. It gives the contractor’s name and tax ID. Do not upload the Social Security number into NextCompli.',
      es: 'Formulario del IRS que llena un contratista de EE.UU. antes de que usted le pague. Trae el nombre y el ID fiscal del contratista. No suba el número de Seguro Social a NextCompli.',
    },
  },
  {
    id: 'w8ben',
    pattern: /\bW-8BEN\b/g,
    label: { en: 'W-8BEN', es: 'W-8BEN' },
    help: {
      en: 'IRS form a person outside the United States gives you before you pay them. It is the foreign-contractor counterpart to a W-9. Do not upload the foreign tax ID into NextCompli.',
      es: 'Formulario del IRS que entrega una persona fuera de Estados Unidos antes de que usted le pague. Es el equivalente del W-9 para un contratista extranjero. No suba el ID fiscal extranjero a NextCompli.',
    },
  },
  {
    id: 'eft',
    pattern: /\bEFT\b/g,
    label: { en: 'EFT', es: 'EFT' },
    help: {
      en: 'Electronic funds transfer: how SAM pays the company. Enter the business-checking last-four only. Do not paste routing or the full account number into NextCompli.',
      es: 'Transferencia electrónica: cómo SAM le paga a la empresa. Escriba solo los últimos cuatro de la cuenta. No pegue la ruta ni el número completo en NextCompli.',
    },
  },
  {
    id: 'gsa',
    pattern: /\bGSA\b/g,
    label: { en: 'GSA', es: 'GSA' },
    help: {
      en: 'General Services Administration. It validates SAM entity registrations. It also awards Multiple Award Schedule contracts. A Schedule is a contract you win, not a free vendor listing. Do not say the company holds one until GSA awards it.',
      es: 'Administración de Servicios Generales. Valida los registros SAM. También adjudica contratos de la Lista de Adjudicación Múltiple. Una lista es un contrato que se gana, no un registro gratis. No diga que la empresa la tiene hasta que GSA la adjudique.',
    },
  },
  {
    id: 'hubzone',
    pattern: /\bHUBZone\b/g,
    label: { en: 'HUBZone', es: 'HUBZone' },
    help: {
      en: 'SBA program for firms in Historically Underutilized Business Zones. Do not claim it until SBA certifies the company.',
      es: 'Programa de la SBA para empresas en zonas históricamente subutilizadas. No lo reclame hasta que la SBA certifique a la empresa.',
    },
  },
  {
    id: 'hub',
    pattern: /\bHUB\b/g,
    label: { en: 'HUB', es: 'HUB' },
    help: {
      en: 'Texas Comptroller vendor preference. As of May 12, 2026 the program is VetHUB, not a race or sex certification. Do not claim HUB until the Comptroller certifies the company.',
      es: 'Preferencia de proveedores del Contralor de Texas. Desde el 12 de mayo de 2026 el programa es VetHUB, no una certificación por raza o sexo. No reclame HUB hasta que el Contralor certifique a la empresa.',
    },
  },
  {
    id: 'vethub',
    pattern: /\bVetHUB\b/g,
    label: { en: 'VetHUB', es: 'VetHUB' },
    help: {
      en: 'Texas Veteran Heroes United in Business. At least 51 percent owned, managed, and run by a Texas resident who is a service-disabled veteran with a disability rating of at least 20 percent. A paper majority without that control does not qualify.',
      es: 'Programa de Texas para veteranos. Al menos 51 por ciento de propiedad, gestión y operación por un residente de Texas que es veterano con discapacidad de servicio de al menos 20 por ciento. Una mayoría en papel sin ese control no califica.',
    },
  },
  {
    id: 'txRamp',
    pattern: /\bTX-RAMP\b/g,
    label: { en: 'TX-RAMP', es: 'TX-RAMP' },
    help: {
      en: 'Texas Risk and Authorization Management Program: the security review a cloud subscription needs before a Texas state agency, public university, or community college stores agency data in it. Level 1 is the Low bar (public or nonconfidential). Level 2 is the Normal bar for confidential records and is also what Texas uses for High impact, plus extra controls. Labor consulting on an agency’s own systems is usually not TX-RAMP.',
      es: 'Programa de Texas de gestión de riesgo y autorización: la revisión de seguridad que una suscripción en la nube necesita antes de que una agencia estatal, universidad pública o colegio comunitario guarde datos de la agencia. El nivel 1 es el nivel Bajo (público o no confidencial). El nivel 2 es el nivel Normal para registros confidenciales y es también el que Texas usa para impacto Alto, más controles extra. La consultoría de mano de obra en los sistemas de una agencia no suele ser TX-RAMP.',
    },
  },
  {
    id: 'fedramp',
    pattern: /\bFedRAMP\b/g,
    label: { en: 'FedRAMP', es: 'FedRAMP' },
    help: {
      en: 'Federal Risk and Authorization Management Program: the security review for cloud services sold to federal agencies. Moderate is the normal bar for day-to-day confidential records. Low only covers public or nonconfidential data. High is for law-enforcement or national-security impact.',
      es: 'Programa federal de gestión de riesgo y autorización: la revisión de seguridad de servicios en la nube vendidos a agencias federales. Moderate es el nivel normal para registros confidenciales del día a día. Low solo cubre datos públicos o no confidenciales. High es para impacto de orden público o seguridad nacional.',
    },
  },
  {
    id: 'soc2',
    pattern: /\bSOC\s*2\b/g,
    label: { en: 'SOC 2', es: 'SOC 2' },
    help: {
      en: 'An AICPA report from a CPA firm about the Trust Services Criteria. Type II tests whether controls operated over a period. Cities and schools often ask for it. It does not create a CMMC status, a SPRS score, or a FedRAMP authorization.',
      es: 'Un informe del AICPA, emitido por un despacho de CPA, sobre los Criterios de Servicios de Confianza. El Tipo II prueba si los controles operaron durante un período. Las ciudades y las escuelas suelen pedirlo. No crea un estatus CMMC, una puntuación SPRS ni una autorización FedRAMP.',
    },
  },
  {
    id: 'tsc',
    pattern: /\bTrust Services Criteria\b|\bCriterios de Servicios de Confianza\b/g,
    label: { en: 'Trust Services Criteria', es: 'Criterios de Servicios de Confianza' },
    help: {
      en: 'The AICPA criteria a SOC 2 report is measured against: Security, and Availability, Confidentiality, Processing Integrity, or Privacy only when you include them. They are outcome-based. They are not the 110 NIST SP 800-171 requirements.',
      es: 'Los criterios del AICPA con los que se mide un informe SOC 2: Seguridad, y Disponibilidad, Confidencialidad, Integridad del procesamiento o Privacidad solo cuando se incluyen. Son criterios de resultado. No son los 110 requisitos de NIST SP 800-171.',
    },
  },
  {
    id: 'fips',
    pattern: /\bFIPS\b/g,
    label: { en: 'FIPS', es: 'FIPS' },
    help: {
      en: 'Federal Information Processing Standards. When CUI is in scope, NIST SP 800-171 expects FIPS-validated encryption. A SOC 2 report does not require that specific validation.',
      es: 'Estándares federales de procesamiento de información. Cuando hay CUI en alcance, NIST SP 800-171 exige cifrado validado por FIPS. Un informe SOC 2 no exige esa validación específica.',
    },
  },
  {
    id: 'taa',
    pattern: /\bTAA\b|\bTrade Agreements Act\b/g,
    label: { en: 'TAA', es: 'TAA' },
    help: {
      en: 'Trade Agreements Act. On a GSA Schedule, products and services must be U.S.-made or from a designated country under FAR 52.225-5. Keep the country of origin. Do not invent one.',
      es: 'Ley de Acuerdos Comerciales. En una lista de GSA, los productos y servicios deben ser de EE. UU. o de un país designado bajo FAR 52.225-5. Conserve el país de origen. No lo invente.',
    },
  },
  {
    id: 'gsar',
    pattern: /\bGSAR\b/g,
    label: { en: 'GSAR', es: 'GSAR' },
    help: {
      en: 'General Services Administration Acquisition Regulation. GSAR 552.238-79 sets the Multiple Award Schedule sales floor: $100,000 in the first five years and $125,000 in each five-year period after that.',
      es: 'Reglamento de adquisiciones de la GSA. GSAR 552.238-79 fija el piso de ventas de la Lista de Adjudicación Múltiple: $100,000 en los primeros cinco años y $125,000 en cada período de cinco años después.',
    },
  },
  {
    id: 'govramp',
    pattern: /\bGovRAMP\b|\bStateRAMP\b/g,
    label: { en: 'GovRAMP', es: 'GovRAMP' },
    help: {
      en: 'A multi-state cloud security review (formerly StateRAMP). Texas DIR can treat a matching GovRAMP authorization like FedRAMP for TX-RAMP reciprocity, but only after you ask in SPECTRIM. It is not automatic.',
      es: 'Una revisión de seguridad en la nube de varios estados (antes StateRAMP). DIR de Texas puede tratar una autorización GovRAMP equivalente como FedRAMP para reciprocidad TX-RAMP, pero solo si usted la pide en SPECTRIM. No es automática.',
    },
  },
  {
    id: 'spectrim',
    pattern: /\bSPECTRIM\b/g,
    label: { en: 'SPECTRIM', es: 'SPECTRIM' },
    help: {
      en: 'The Texas DIR portal where a cloud vendor files a TX-RAMP request, including reciprocity from FedRAMP or GovRAMP. After 30 Oct 2024 those other authorizations are not added to the Texas list unless you submit this form.',
      es: 'El portal de DIR de Texas donde un proveedor de nube presenta una solicitud TX-RAMP, incluida la reciprocidad de FedRAMP o GovRAMP. Después del 30 de oct. de 2024 esas otras autorizaciones no se agregan a la lista de Texas salvo que envíe este formulario.',
    },
  },
  {
    id: 'saeps',
    pattern: /\bSAePS\b/g,
    label: { en: 'SAePS', es: 'SAePS' },
    help: {
      en: 'San Antonio e-Procurement System: the City vendor portal for goods and professional services, including IT. One account per vendor. Construction uses CIVCAST instead.',
      es: 'Sistema de e-adquisiciones de San Antonio: el portal de proveedores de la Ciudad para bienes y servicios profesionales, incluida TI. Una cuenta por proveedor. La construcción usa CIVCAST.',
    },
  },
  {
    id: 'civcast',
    pattern: /\bCIVCAST\b/g,
    label: { en: 'CIVCAST', es: 'CIVCAST' },
    help: {
      en: 'City of San Antonio portal for construction, architecture, and engineering bids. IT and professional-services work stays on SAePS, not CIVCAST.',
      es: 'Portal de la Ciudad de San Antonio para obras, arquitectura e ingeniería. El trabajo de TI y servicios profesionales permanece en SAePS, no en CIVCAST.',
    },
  },
  {
    id: 'sbir',
    pattern: /\bSBIR\b/g,
    label: { en: 'SBIR', es: 'SBIR' },
    help: {
      en: 'Small Business Innovation Research: federal research money for a for-profit U.S. small business. Phase I proves the idea, Phase II builds a prototype, and Phase III sells it with other money. The company does not give up ownership.',
      es: 'Investigación de Innovación para Pequeñas Empresas: dinero federal de investigación para una pequeña empresa con fines de lucro de EE. UU. La Fase I prueba la idea, la Fase II construye un prototipo y la Fase III lo vende con otro dinero. La empresa no cede la propiedad.',
    },
  },
  {
    id: 'sttr',
    pattern: /\bSTTR\b/g,
    label: { en: 'STTR', es: 'STTR' },
    help: {
      en: 'Small Business Technology Transfer: the same three phases as SBIR, but the small business must partner with a nonprofit research institution. The business still applies and stays in charge.',
      es: 'Transferencia de Tecnología para Pequeñas Empresas: las mismas tres fases que SBIR, pero la pequeña empresa debe asociarse con una institución de investigación sin fines de lucro. La empresa sigue siendo quien solicita y quien dirige.',
    },
  },
  {
    id: 'dsip',
    pattern: /\bDSIP\b/g,
    label: { en: 'DSIP', es: 'DSIP' },
    help: {
      en: 'Defense SBIR/STTR Innovation Portal: the site where a company submits a defense research proposal. It is at dodsbirsttr.mil. NextCompli does not submit the proposal.',
      es: 'Portal de Innovación SBIR/STTR de Defensa: el sitio donde una empresa presenta una propuesta de investigación de defensa. Está en dodsbirsttr.mil. NextCompli no presenta la propuesta.',
    },
  },
  {
    id: 'aiRmf',
    pattern: /\bAI RMF\b/g,
    label: { en: 'AI RMF', es: 'AI RMF' },
    help: {
      en: 'NIST Artificial Intelligence Risk Management Framework, version 1.0. A voluntary guide with four jobs: govern, map, measure, and manage. It is not a certificate.',
      es: 'Marco de Gestión de Riesgos de Inteligencia Artificial del NIST, versión 1.0. Una guía voluntaria con cuatro tareas: gobernar, mapear, medir y gestionar. No es un certificado.',
    },
  },
  {
    id: 'iso27001',
    pattern: /\bISO(?:\/IEC)? 27001\b/g,
    label: { en: 'ISO 27001', es: 'ISO 27001' },
    help: {
      en: 'The international standard for an information-security management system. A website record is not an ISO 27001 certificate.',
      es: 'La norma internacional de un sistema de gestión de seguridad de la información. Un registro del sitio no es un certificado ISO 27001.',
    },
  },
  {
    id: 'iso27701',
    pattern: /\bISO(?:\/IEC)? 27701\b/g,
    label: { en: 'ISO 27701', es: 'ISO 27701' },
    help: {
      en: 'The privacy extension to ISO 27001. It covers how personal data is handled. This checklist does not certify it.',
      es: 'La extensión de privacidad de ISO 27001. Cubre cómo se trata la información personal. Esta lista no la certifica.',
    },
  },
  {
    id: 'en301549',
    pattern: /\bEN 301 549\b/g,
    label: { en: 'EN 301 549', es: 'EN 301 549' },
    help: {
      en: 'The European standard for accessible technology. For websites it follows WCAG. It is not a U.S. Section 508 certificate.',
      es: 'La norma europea de tecnología accesible. Para sitios web sigue WCAG. No es un certificado de la Sección 508 de EE. UU.',
    },
  },
  {
    id: 'iso42001',
    pattern: /\bISO\/IEC 42001\b/g,
    label: { en: 'ISO/IEC 42001', es: 'ISO/IEC 42001' },
    help: {
      en: 'The international standard for an AI management system. A company can seek a certificate later. This checklist does not issue that certificate.',
      es: 'La norma internacional de un sistema de gestión de IA. Una empresa puede buscar un certificado después. Esta lista no emite ese certificado.',
    },
  },
  {
    id: 'omb',
    pattern: /\bOMB\b/g,
    label: { en: 'OMB', es: 'OMB' },
    help: {
      en: 'Office of Management and Budget: the White House office that tells federal agencies how to run their own AI use. Memorandum M-24-10 binds agencies, not every contractor.',
      es: 'Oficina de Administración y Presupuesto: la oficina de la Casa Blanca que dice a las agencias federales cómo manejar su propio uso de IA. El memorando M-24-10 obliga a las agencias, no a todo contratista.',
    },
  },
  {
    id: 'apex',
    pattern: /\bAPEX Accelerator\b/g,
    label: { en: 'APEX Accelerator', es: 'APEX Accelerator' },
    help: {
      en: 'A free Small Business Administration office that shows a small company how to sell to the government. You book a meeting. They can look at your vendor registration, a one-page description of the work, and the plan for the first bid. NextCompli does not book the meeting.',
      es: 'Una oficina gratuita de la Administración de Pequeños Negocios que enseña a una empresa pequeña a venderle al gobierno. Usted reserva una cita. Pueden revisar el registro de proveedor, una descripción de una página del trabajo y el plan de la primera oferta. NextCompli no reserva la cita.',
    },
  },
  {
    id: 'sba',
    pattern: /\bSBA\b/g,
    label: { en: 'SBA', es: 'SBA' },
    help: {
      en: 'U.S. Small Business Administration: the federal office that runs small-business size standards and certifications such as 8(a), WOSB, and HUBZone.',
      es: 'Administración de Pequeños Negocios de EE. UU.: la oficina federal de tamaños de pequeña empresa y certificaciones como 8(a), WOSB y HUBZone.',
    },
  },
  {
    id: 'dsbs',
    pattern: /\bDSBS\b/g,
    label: { en: 'DSBS', es: 'DSBS' },
    help: {
      en: 'Dynamic Small Business Search: the old name of the SBA public directory. Primes now search Small Business Search (SBS) at search.certifications.sba.gov after SAM is Active.',
      es: 'Búsqueda dinámica de pequeñas empresas: el nombre anterior del directorio público de la SBA. Los primes ahora buscan en Small Business Search (SBS) en search.certifications.sba.gov cuando SAM está Active.',
    },
  },
  {
    id: 'sbs',
    pattern: /\bSBS\b(?=,|\s+\(|\s+formerly)/g,
    label: { en: 'SBS', es: 'SBS' },
    help: {
      en: 'Small Business Search: the SBA public directory primes use to find certified small firms. It replaced DSBS. Complete it only after SAM is Active. Do not invent a profile URL.',
      es: 'Búsqueda de Pequeñas Empresas: el directorio público de la SBA que los primes usan para hallar firmas pequeñas certificadas. Reemplazó a DSBS. Complételo solo cuando SAM esté Active. No invente una URL de perfil.',
    },
  },
  {
    id: 'wosb',
    pattern: /\bEDWOSB\b|\bWOSB\b/g,
    label: { en: 'WOSB', es: 'WOSB' },
    help: {
      en: 'Women-Owned Small Business (and EDWOSB, the economically disadvantaged variant). It needs at least 51 percent ownership and real control by women. Do not claim it until SBA certifies the company.',
      es: 'Pequeña empresa de mujeres (y EDWOSB, la variante económicamente desfavorecida). Exige al menos 51 por ciento de propiedad y control real de mujeres. No lo reclame hasta que la SBA certifique a la empresa.',
    },
  },
  {
    id: 'eightA',
    pattern: /\b8\(a\)\b/g,
    label: { en: '8(a)', es: '8(a)' },
    help: {
      en: 'SBA 8(a) Business Development program. It usually needs two years in business and an SBA application. Do not claim 8(a) until SBA awards it. An 8(a) joint venture is a separate company.',
      es: 'Programa 8(a) de desarrollo empresarial de la SBA. Suele exigir dos años de operación y una solicitud. No reclame 8(a) hasta que la SBA lo otorgue. Una empresa conjunta 8(a) es otra empresa.',
    },
  },
  {
    id: 'fcl',
    pattern: /\bFCL\b|facility clearance/gi,
    label: { en: 'FCL', es: 'FCL' },
    help: {
      en: 'Facility clearance: permission for the company to hold classified (Secret / Top Secret) work. It is not how you get CMMC Level 2, and it is not required for FCI or CUI alone. An owner’s personal clearance is not the company’s FCL. Do not sponsor one unless a classified notice requires it.',
      es: 'Habilitación de instalaciones: permiso para que la empresa maneje trabajo clasificado (Secret / Top Secret). No es la forma de obtener CMMC Nivel 2, y no se exige solo por FCI o CUI. La habilitación personal de un dueño no es el FCL de la empresa. No la gestione salvo que un aviso clasificado la exija.',
    },
  },
  {
    id: 'dd254',
    pattern: /\bDD Form 254\b/g,
    label: { en: 'DD Form 254', es: 'DD Form 254' },
    help: {
      en: 'The form that states the security level of one contract. It is not a facility clearance and it is not a person’s clearance.',
      es: 'El formulario que indica el nivel de seguridad de un contrato. No es una habilitación de instalaciones ni la habilitación de una persona.',
    },
  },
  {
    id: 'ato',
    pattern: /\bATO\b/g,
    label: { en: 'ATO', es: 'ATO' },
    help: {
      en: 'Authority to operate: a government decision that a system may run. A cyber policy and a CMMC level do not create an ATO.',
      es: 'Autoridad para operar: una decisión del gobierno de que un sistema puede funcionar. Una póliza ciber y un nivel CMMC no crean un ATO.',
    },
  },
  {
    id: 'hacs',
    pattern: /\bHACS\b/g,
    label: { en: 'HACS', es: 'HACS' },
    help: {
      en: 'Highly Adaptive Cybersecurity Services, NAICS 54151. Some cyber notices require it. Do not claim it unless SAM lists that code for this company.',
      es: 'Servicios de ciberseguridad altamente adaptativos, NAICS 54151. Algunos avisos ciber lo exigen. No lo reclame salvo que SAM liste ese código para esta empresa.',
    },
  },
  {
    id: 'oasis',
    pattern: /OASIS\+/g,
    label: { en: 'OASIS+', es: 'OASIS+' },
    help: {
      en: 'A government-wide professional-services contract vehicle. It is won after SAM is Active. It is not a signup. Do not copy a prime’s OASIS+ number onto your capability statement.',
      es: 'Un vehículo de servicios profesionales para todo el gobierno. Se gana cuando SAM está Active. No es un alta. No copie el número OASIS+ de un prime en su declaración de capacidades.',
    },
  },
  {
    id: 'nigp',
    pattern: /\bNIGP\b/g,
    label: { en: 'NIGP', es: 'NIGP' },
    help: {
      en: 'Commodity codes cities, counties, and the Texas CMBL use so you get the right solicitations. SAM.gov uses NAICS, not NIGP. Pick only codes the company can actually perform.',
      es: 'Códigos de mercancía que ciudades, condados y el CMBL de Texas usan para enviarle las licitaciones correctas. SAM.gov usa NAICS, no NIGP. Elija solo códigos que la empresa realmente pueda cumplir.',
    },
  },
  {
    id: 'dir',
    pattern: /\bDIR\b(?= |\.|$|,)/g,
    label: { en: 'DIR', es: 'DIR' },
    help: {
      en: 'Texas Department of Information Resources: pre-competed state IT contracts cities and schools can buy from. Do not invent DIR status.',
      es: 'Departamento de Recursos de Información de Texas: contratos de TI precompetidos que ciudades y escuelas pueden usar. No invente un estatus DIR.',
    },
  },
  {
    id: 'esbd',
    pattern: /\bESBD\b/g,
    label: { en: 'ESBD', es: 'ESBD' },
    help: {
      en: 'Electronic State Business Daily: the Texas board where state agencies and some local governments post solicitations. Watching it is not a contract and not a HUB certification.',
      es: 'Boletín electrónico de negocios del estado: el tablero de Texas donde agencias estatales y algunos gobiernos locales publican licitaciones. Mirarlo no es un contrato ni una certificación HUB.',
    },
  },
  {
    id: 'cmbl',
    pattern: /\bCMBL\b/g,
    label: { en: 'CMBL', es: 'CMBL' },
    help: {
      en: 'Centralized Master Bidders List: the Texas Comptroller list that emails bid notices for the class codes you pick. The published annual fee is $70 and an EIN is required. It is not a DIR contract.',
      es: 'Lista maestra centralizada de postores: la lista del Contralor de Texas que envía avisos de licitación según los códigos que elija. La tarifa anual publicada es $70 y exige un EIN. No es un contrato DIR.',
    },
  },
  {
    id: 'vpts',
    pattern: /\bVPTS\b/g,
    label: { en: 'VPTS', es: 'VPTS' },
    help: {
      en: 'Vendor Performance Tracking System: the Texas Comptroller scorecard on how a vendor performed on state contracts. Check it after the first state award; it is not a signup list.',
      es: 'Sistema de seguimiento de desempeño de proveedores: la tarjeta del Contralor de Texas sobre cómo un proveedor cumplió contratos estatales. Consúltelo después del primer premio estatal; no es una lista de alta.',
    },
  },
  {
    id: 'franchiseTax',
    pattern: /franchise tax account status/gi,
    label: { en: 'franchise tax account status', es: 'estatus de cuenta de impuesto de franquicia' },
    help: {
      en: 'The Texas Comptroller lookup that shows whether the LLC may transact business in Texas. The Comptroller no longer calls this a certificate of good standing. The SOS Certificate of Formation is a different document.',
      es: 'La consulta del Contralor de Texas que muestra si la LLC puede realizar negocios en Texas. El Contralor ya no llama a esto un certificado de good standing. El certificado de constitución del SOS es otro documento.',
    },
  },
  {
    id: 'buyboard',
    pattern: /\bBuyBoard\b/g,
    label: { en: 'BuyBoard', es: 'BuyBoard' },
    help: {
      en: 'A Texas purchasing cooperative that cities, counties, and schools buy from. You win a seat by answering their solicitation. Registration is not an award. Do not invent a contract number.',
      es: 'Una cooperativa de compras de Texas de la que compran ciudades, condados y escuelas. Se gana un asiento respondiendo su licitación. El registro no es una adjudicación. No invente un número de contrato.',
    },
  },
  {
    id: 'tips',
    pattern: /\bTIPS\b/g,
    label: { en: 'TIPS', es: 'TIPS' },
    help: {
      en: 'The Interlocal Purchasing System (Region 8 ESC): another optional cooperative catalog. You win a seat by answering their solicitation. Registration is not an award.',
      es: 'The Interlocal Purchasing System (ESC Región 8): otro catálogo cooperativo opcional. Se gana un asiento respondiendo su licitación. El registro no es una adjudicación.',
    },
  },
  {
    id: 'hgacBuy',
    pattern: /\bHGACBuy\b/g,
    label: { en: 'HGACBuy', es: 'HGACBuy' },
    help: {
      en: 'Houston-Galveston Area Council cooperative purchasing catalog. Cities and schools can buy from awarded vendors. Winning a seat is not the same as registering in SAM.',
      es: 'Catálogo cooperativo del Consejo del Área Houston-Galveston. Ciudades y escuelas pueden comprar a los proveedores adjudicados. Ganar un asiento no es lo mismo que registrarse en SAM.',
    },
  },
  {
    id: 'bidnet',
    pattern: /\bBidNet\b/g,
    label: { en: 'BidNet', es: 'BidNet' },
    help: {
      en: 'BidNet Direct: a bid board some Texas counties use instead of that county’s own portal (for example Hays County). Register once per portal. Store the login in your password manager.',
      es: 'BidNet Direct: un tablero de licitaciones que algunos condados de Texas usan en vez del portal propio de ese condado (por ejemplo el Condado de Hays). Regístrese una vez por portal. Guarde el acceso en su administrador de contraseñas.',
    },
  },
  {
    id: 'sam',
    pattern: /\bSAM(?:\.gov)?\b/g,
    label: { en: 'SAM.gov', es: 'SAM.gov' },
    help: {
      en: 'System for Award Management: the site where a company must be Active with a UEI and CAGE before it can bid on most federal work.',
      es: 'Sistema de gestión de adjudicaciones: el sitio donde la empresa debe estar Active con UEI y CAGE para licitar la mayoría del trabajo federal.',
    },
  },
  {
    id: 'llc',
    pattern: /\bLLCs?\b/g,
    label: { en: 'LLC', es: 'LLC' },
    help: {
      en: 'Limited liability company: the legal entity that bids. Put this legal name on SAM, the bank account, and insurance — not only the DBA.',
      es: 'Sociedad de responsabilidad limitada: la entidad legal que licita. Use este nombre legal en SAM, el banco y el seguro, no solo el DBA.',
    },
  },
  {
    id: 'c3pao',
    pattern: /\bC3PAOs?\b/g,
    label: { en: 'C3PAO', es: 'C3PAO' },
    help: {
      en: 'Certified Third-Party Assessment Organization: an outside auditor for CMMC Level 2. DoD paused those third-party assessments in July 2026.',
      es: 'Organización de evaluación de terceros certificada: un auditor externo para CMMC Nivel 2. El DoD pausó esas evaluaciones en julio de 2026.',
    },
  },
  {
    id: 'gccHigh',
    pattern: /\bGCC High\b/g,
    label: { en: 'GCC High', es: 'GCC High' },
    help: {
      en: 'Microsoft’s U.S. government cloud. Used when a contract handles CUI. FCI-only work does not need it.',
      es: 'La nube gubernamental de Microsoft en EE.UU. Se usa cuando el contrato maneja CUI. El trabajo solo FCI no la necesita.',
    },
  },
  {
    id: 'nist',
    pattern: /\bNIST(?:\s*800-171)?\b/g,
    label: { en: 'NIST 800-171', es: 'NIST 800-171' },
    help: {
      en: 'National Institute of Standards and Technology control set for protecting CUI. Not required for FCI-only / CMMC Level 1 work.',
      es: 'Controles del Instituto Nacional de Estándares y Tecnología para proteger CUI. No se exige para trabajo solo FCI / CMMC Nivel 1.',
    },
  },
  {
    id: 'cmmc',
    pattern: /\bCMMC\b/g,
    label: { en: 'CMMC', es: 'CMMC' },
    help: {
      en: 'Cybersecurity Maturity Model Certification: DoD’s cyber program for contractors. Level 1 is a self-check for FCI. Level 2 is the 110 NIST 800-171 practices for CUI. A facility clearance is a separate gate for classified work, not a CMMC level.',
      es: 'Certificación de madurez de ciberseguridad: el programa ciber del DoD para contratistas. El Nivel 1 es una autoevaluación para FCI. El Nivel 2 son las 110 prácticas NIST 800-171 para CUI. Una habilitación de instalaciones es otra puerta para trabajo clasificado, no un nivel CMMC.',
    },
  },
  {
    id: 'fci',
    pattern: /\bFCI\b/g,
    label: { en: 'FCI', es: 'FCI' },
    help: {
      en: 'Federal Contract Information: unclassified government data under a contract that is not for the public. First bar is CMMC Level 1. It is not CUI and it is not classified, so it does not by itself require CMMC Level 2 or a facility clearance.',
      es: 'Información de contratos federales: datos gubernamentales no clasificados de un contrato que no son para el público. La primera barra es CMMC Nivel 1. No es CUI ni clasificada, así que por sí sola no exige CMMC Nivel 2 ni una habilitación de instalaciones.',
    },
  },
  {
    id: 'cui',
    pattern: /\bCUI\b/g,
    label: { en: 'CUI', es: 'CUI' },
    help: {
      en: 'Controlled Unclassified Information: more sensitive than FCI, still not Secret or Top Secret. Needs NIST 800-171 / CMMC Level 2. Look for those words (or DFARS 252.204-7012) in the notice. Do not bid CUI until that path is real.',
      es: 'Información controlada no clasificada: más sensible que FCI, todavía no es Secret ni Top Secret. Requiere NIST 800-171 / CMMC Nivel 2. Busque esas palabras (o DFARS 252.204-7012) en el aviso. No oferte CUI hasta tener esa ruta.',
    },
  },
  {
    id: 'sprs',
    pattern: /\bSPRS\b/g,
    label: { en: 'SPRS', es: 'SPRS' },
    help: {
      en: 'Supplier Performance Risk System: the DoD site where a senior official posts and affirms the company’s CMMC / NIST score.',
      es: 'Sistema de riesgo de desempeño de proveedores: el sitio del DoD donde un directivo publica y afirma la puntuación CMMC / NIST de la empresa.',
    },
  },
  {
    id: 'dfars',
    pattern: /\bDFARS\b/g,
    label: { en: 'DFARS', es: 'DFARS' },
    help: {
      en: 'Defense Federal Acquisition Regulation Supplement: extra DoD contract rules on top of FAR, including cyber clauses.',
      es: 'Suplemento de la regulación de adquisiciones de defensa: reglas extra del DoD sobre el FAR, incluidas las cláusulas de ciberseguridad.',
    },
  },
  {
    id: 'poam',
    pattern: /\bPOA&Ms?\b|\bPOAMs?\b/g,
    label: { en: 'POA&M', es: 'POA&M' },
    help: {
      en: 'Plan of Action and Milestones: a list of cyber gaps still to fix. CMMC Level 1 is pass/fail — no POA&M credit.',
      es: 'Plan de acción y hitos: lista de brechas ciber que aún hay que cerrar. CMMC Nivel 1 es apto/no apto: no hay crédito POA&M.',
    },
  },
  {
    id: 'ssp',
    pattern: /\bSSP\b/g,
    label: { en: 'SSP', es: 'SSP' },
    help: {
      en: 'System Security Plan: the written description of how you protect CUI systems. Not required to claim CMMC Level 1.',
      es: 'Plan de seguridad del sistema: la descripción escrita de cómo protege los sistemas CUI. No se exige para reclamar CMMC Nivel 1.',
    },
  },
  {
    id: 'far',
    pattern: /\bFAR\b/g,
    label: { en: 'FAR', es: 'FAR' },
    help: {
      en: 'Federal Acquisition Regulation: the baseline U.S. government contracting rulebook. Examples: FAR 52.204-21 (FCI safeguards) and FAR 52.228-5 (keep proof of insurance in the contract file).',
      es: 'Regulación federal de adquisiciones: el reglamento base de contratos del gobierno de EE.UU. Ejemplos: FAR 52.204-21 (salvaguardas FCI) y FAR 52.228-5 (guardar prueba de seguro en el expediente).',
    },
  },
  {
    id: 'duns',
    pattern: /\bDUNS\b/g,
    label: { en: 'DUNS', es: 'DUNS' },
    help: {
      en: 'An old Dun & Bradstreet company ID. SAM.gov now issues a UEI instead. Do not buy a DUNS for federal work.',
      es: 'Un ID antiguo de Dun & Bradstreet. SAM.gov ahora emite un UEI. No compre un DUNS para trabajo federal.',
    },
  },
  {
    id: 'kpi',
    pattern: /\bKPIs?\b/g,
    label: { en: 'KPI', es: 'KPI' },
    help: {
      en: 'Key performance indicator: a count on this page that shows whether the work is on track, such as how many steps are open, blocked, or done. It is not a legal case number and not an audit score.',
      es: 'Indicador clave de desempeño: un conteo en esta página que muestra si el trabajo va en curso, como cuántos pasos están abiertos, bloqueados o hechos. No es un número de caso legal ni un puntaje de auditoría.',
    },
  },
  {
    id: 'dnsbl',
    pattern: /\bDNSBL\b/g,
    label: { en: 'DNSBL', es: 'DNSBL' },
    help: {
      en: 'A public block list of mail servers that other companies have reported for spam. Mailboxes check it before they accept a message. A listing is about that server, not a score for the whole company.',
      es: 'Una lista pública de servidores de correo que otras empresas reportaron por spam. Los buzones la consultan antes de aceptar un mensaje. Un listado es de ese servidor, no un puntaje de toda la empresa.',
    },
  },
  {
    id: 'spf',
    pattern: /\bSPF\b/g,
    label: { en: 'SPF', es: 'SPF' },
    help: {
      en: 'Sender Policy Framework: a DNS record listing which mail servers may send email as your domain. Mailboxes use it to keep spoofed mail out of the inbox.',
      es: 'Sender Policy Framework: un registro DNS que lista qué servidores pueden enviar correo como su dominio. Los buzones lo usan para que el correo falsificado no entre a la bandeja.',
    },
  },
  {
    id: 'dkim',
    pattern: /\bDKIM\b/g,
    label: { en: 'DKIM', es: 'DKIM' },
    help: {
      en: 'DomainKeys Identified Mail: a cryptographic signature on each message so the receiver can prove the sending domain actually sent it and it was not altered.',
      es: 'DomainKeys Identified Mail: una firma criptográfica en cada mensaje para que el receptor compruebe que el dominio realmente lo envió y no fue alterado.',
    },
  },
  {
    id: 'dmarc',
    pattern: /\bDMARC\b/g,
    label: { en: 'DMARC', es: 'DMARC' },
    help: {
      en: 'Domain-based Message Authentication, Reporting, and Conformance: a DNS policy that tells Gmail, Yahoo, and Microsoft what to do when SPF or DKIM fail, and sends you reports of who is sending as you.',
      es: 'Autenticación, informes y conformidad de mensajes basados en dominio: una política DNS que dice a Gmail, Yahoo y Microsoft qué hacer cuando fallan SPF o DKIM, y le envía informes de quién envía como usted.',
    },
  },
  {
    id: 'bimi',
    pattern: /\bBIMI\b/g,
    label: { en: 'BIMI', es: 'BIMI' },
    help: {
      en: 'Brand Indicators for Message Identification: shows your logo next to authenticated mail. Needs DMARC at quarantine or reject, plus usually a Verified Mark Certificate.',
      es: 'Indicadores de marca para identificación de mensajes: muestra su logo junto al correo autenticado. Requiere DMARC en cuarentena o rechazo y, por lo general, un certificado de marca verificada.',
    },
  },
  {
    id: 'zeroTrust',
    pattern: /\bZero Trust\b/gi,
    label: { en: 'Zero Trust', es: 'Zero Trust' },
    help: {
      en: 'A security model that never trusts a user, device, or network by default. Every access is verified with identity, device health, and least privilege. CISA publishes a maturity model for it.',
      es: 'Un modelo de seguridad que no confía por defecto en un usuario, dispositivo o red. Cada acceso se verifica con identidad, estado del dispositivo y mínimo privilegio. CISA publica un modelo de madurez.',
    },
  },
  {
    id: 'cisa',
    pattern: /\bCISA\b/g,
    label: { en: 'CISA', es: 'CISA' },
    help: {
      en: 'Cybersecurity and Infrastructure Security Agency: the U.S. civilian cyber agency. Its Zero Trust Maturity Model is the public playbook for identity, devices, networks, apps, and data.',
      es: 'Agencia de Ciberseguridad e Infraestructura: la agencia civil de ciberseguridad de EE.UU. Su modelo de madurez Zero Trust es la guía pública para identidad, dispositivos, redes, apps y datos.',
    },
  },
  {
    id: 'cis',
    pattern: /\bCIS\b/g,
    label: { en: 'CIS', es: 'CIS' },
    help: {
      en: 'Center for Internet Security: publishes the CIS Controls and CIS Benchmarks. Implementation Groups IG1–IG3 are the usual small / mid / high-assurance ladders.',
      es: 'Center for Internet Security: publica los controles y puntos de referencia CIS. Los grupos de implementación IG1–IG3 son la escala habitual de higiene esencial / media / alta.',
    },
  },
  {
    id: 'asvs',
    pattern: /\bASVS\b/g,
    label: { en: 'ASVS', es: 'ASVS' },
    help: {
      en: 'OWASP Application Security Verification Standard: three website/app assurance levels. Level 1 is the public-web baseline; Level 2 is most apps with sensitive data; Level 3 is high-assurance.',
      es: 'Estándar de verificación de seguridad de aplicaciones de OWASP: tres niveles de aseguramiento. El nivel 1 es la base de un sitio público; el 2 es la mayoría de apps con datos sensibles; el 3 es alta confianza.',
    },
  },
  {
    id: 'mfa',
    pattern: /\bMFA\b/g,
    label: { en: 'MFA', es: 'MFA' },
    help: {
      en: 'Multi-factor authentication: a second proof besides the password, usually a phone prompt or hardware key. The first Zero Trust control for cloud, git, and admin logins.',
      es: 'Autenticación multifactor: una segunda prueba además de la contraseña, por lo general un aviso al teléfono o una llave de hardware. El primer control Zero Trust para nube, git y administradores.',
    },
  },
  {
    id: 'sso',
    pattern: /\bSSO\b/g,
    label: { en: 'SSO', es: 'SSO' },
    help: {
      en: 'Single sign-on: one company login (often SAML or OIDC) for cloud apps and git, so leavers lose access in one place instead of leftover local passwords.',
      es: 'Inicio de sesión único: un login de empresa (a menudo SAML u OIDC) para apps en la nube y git, para que quien sale pierda el acceso en un solo lugar y no queden contraseñas locales.',
    },
  },
  {
    id: 'waf',
    pattern: /\bWAF\b/g,
    label: { en: 'WAF', es: 'WAF' },
    help: {
      en: 'Web application firewall: a filter in front of the website that blocks common attacks such as injection and bot floods before they reach the app.',
      es: 'Cortafuegos de aplicación web: un filtro frente al sitio que bloquea ataques comunes como inyección y bots antes de que lleguen a la aplicación.',
    },
  },
  {
    id: 'cspm',
    pattern: /\bCSPM\b/g,
    label: { en: 'CSPM', es: 'CSPM' },
    help: {
      en: 'Cloud security posture management: continuous checks that cloud accounts match a benchmark (open storage, public databases, unused admin keys) and raise the gaps.',
      es: 'Gestión de postura de seguridad en la nube: comprobaciones continuas de que las cuentas coinciden con un referente (almacenamiento abierto, bases públicas, claves de admin sin uso) y marcan las brechas.',
    },
  },
  {
    id: 'sbom',
    pattern: /\bSBOM\b/g,
    label: { en: 'SBOM', es: 'SBOM' },
    help: {
      en: 'Software bill of materials: the ingredient list of libraries and container images you ship, used to know which known holes apply to you.',
      es: 'Lista de materiales de software: el inventario de librerías e imágenes de contenedor que entrega, para saber qué fallos conocidos le aplican.',
    },
  },
  {
    id: 'ofac',
    pattern: /\bOFAC\b/g,
    label: { en: 'OFAC', es: 'OFAC' },
    help: {
      en: 'Office of Foreign Assets Control: the U.S. Treasury office that writes and enforces economic sanctions. NextCompli screens names against OFAC lists; it is not OFAC counsel.',
      es: 'Oficina de Control de Activos Extranjeros: la oficina del Tesoro de EE. UU. que redacta y aplica sanciones económicas. NextCompli criba nombres contra listas OFAC; no es un abogado de OFAC.',
    },
  },
  {
    id: 'sdn',
    pattern: /\bSDN\b/g,
    label: { en: 'SDN', es: 'SDN' },
    help: {
      en: 'Specially Designated Nationals and Blocked Persons List: people and companies whose property U.S. persons must block. A general license does not skip this check.',
      es: 'Lista de Nacionales Especialmente Designados y Personas Bloqueadas: personas y empresas cuyos bienes las personas de EE. UU. deben bloquear. Una licencia general no omite esta comprobación.',
    },
  },
  {
    id: 'ofac-fifty',
    pattern: /50% rule|regla del 50%/gi,
    label: { en: '50% rule', es: 'regla del 50%' },
    help: {
      en: 'OFAC adds up ownership. When blocked persons own 50% or more of an entity, that entity is blocked too, even when its own name is absent from the SDN list.',
      es: 'OFAC suma la propiedad. Cuando personas bloqueadas poseen el 50% o más de una entidad, esa entidad también queda bloqueada, aunque su nombre no esté en la lista SDN.',
    },
  },
  {
    id: 'capta',
    pattern: /\bCAPTA\b/g,
    label: { en: 'CAPTA', es: 'CAPTA' },
    help: {
      en: 'Correspondent Account or Payable-Through Account sanctions list: foreign banks restricted from (or conditioned on) U.S. correspondent accounts. Not the same as an SDN blocking list.',
      es: 'Lista de sanciones de cuentas corresponsales o payable-through: bancos extranjeros restringidos (o condicionados) respecto a cuentas corresponsales en EE. UU. No es lo mismo que una lista de bloqueo SDN.',
    },
  },
  {
    id: 'dmca',
    pattern: /\bDMCA\b/g,
    label: { en: 'DMCA', es: 'DMCA' },
    help: {
      en: 'Digital Millennium Copyright Act: U.S. copyright rules for websites. Section 512 is the notice-and-takedown process for alleged infringement on a site you host.',
      es: 'Ley de Derechos de Autor del Milenio Digital: reglas de copyright de EE. UU. para sitios. La sección 512 es el proceso de aviso y retirada de contenido presuntamente infractor.',
    },
  },
  {
    id: 'gdpr',
    pattern: /\bGDPR\b/g,
    label: { en: 'GDPR', es: 'RGPD' },
    help: {
      en: 'General Data Protection Regulation: the EU/UK privacy law. If you serve people in Europe, you need a lawful basis, notices, and a way to honor access and deletion requests.',
      es: 'Reglamento General de Protección de Datos: la ley de privacidad de la UE/Reino Unido. Si atiende a personas en Europa, necesita base legal, avisos y forma de atender acceso y borrado.',
    },
  },
  {
    id: 'ccpa',
    pattern: /\bCCPA(?:\/CPRA)?\b|\bCPRA\b/g,
    label: { en: 'CCPA/CPRA', es: 'CCPA/CPRA' },
    help: {
      en: 'California privacy laws. CCPA is the original; CPRA expanded it. They require a privacy notice, Do Not Sell / Limit Sensitive PI links, and a way to answer consumer requests.',
      es: 'Leyes de privacidad de California. CCPA es la original; CPRA la amplió. Piden aviso de privacidad, enlaces de No vender / Limitar información sensible y forma de atender solicitudes.',
    },
  },
  {
    id: 'wcag',
    pattern: /\bWCAG\b/g,
    label: { en: 'WCAG', es: 'WCAG' },
    help: {
      en: 'Web Content Accessibility Guidelines: the shared technical standard for keyboards, screen readers, contrast, and the other checks on a public page. Level AA of version 2.1 is the bar Section 508 uses for web content. An overlay widget is not a substitute.',
      es: 'Pautas de Accesibilidad para el Contenido Web: el estándar técnico de teclado, lectores de pantalla, contraste y las demás revisiones de una página pública. El nivel AA de la versión 2.1 es la barra que la Sección 508 usa para páginas web. Un widget de overlay no sustituye esto.',
    },
  },
  {
    id: 'section-508',
    pattern: /\bSection 508\b/g,
    label: { en: 'Section 508', es: 'Sección 508' },
    help: {
      en: 'The U.S. law that requires federal technology, including public websites a federal buyer uses, to be accessible. The regulation points at WCAG 2.0 Level AA. Current practice is to also meet WCAG 2.2 Level AA. This checklist does not certify either.',
      es: 'La ley de EE. UU. que exige que la tecnología federal, incluidos los sitios públicos que usa un comprador federal, sea accesible. El reglamento señala WCAG 2.0 nivel AA. La práctica actual es cumplir también WCAG 2.2 nivel AA. Esta lista no certifica ninguno.',
    },
  },
  {
    id: 'vpat',
    pattern: /\bVPAT\b/g,
    label: { en: 'VPAT', es: 'VPAT' },
    help: {
      en: 'Voluntary Product Accessibility Template: the form a vendor fills to report how a product meets Section 508. It is not a certificate.',
      es: 'Plantilla voluntaria de accesibilidad del producto: el formulario que llena un proveedor para informar cómo un producto cumple la Sección 508. No es un certificado.',
    },
  },
  {
    id: 'acr',
    pattern: /\bACR\b/g,
    label: { en: 'ACR', es: 'ACR' },
    help: {
      en: 'Accessibility Conformance Report: the filled-in VPAT a buyer can read. It lists gaps. It is not a certificate that the site conforms.',
      es: 'Informe de conformidad de accesibilidad: el VPAT ya lleno que puede leer un comprador. Lista las brechas. No es un certificado de que el sitio cumple.',
    },
  },
  {
    id: 'hipaa',
    pattern: /\bHIPAA\b/g,
    label: { en: 'HIPAA', es: 'HIPAA' },
    help: {
      en: 'Health Insurance Portability and Accountability Act: U.S. rules for health information. They apply when the site collects health details. A normal marketing site writes “Not a health site.” HTTPS alone is not HIPAA.',
      es: 'Ley de portabilidad y responsabilidad del seguro de salud: reglas de EE. UU. para información de salud. Aplican cuando el sitio recoge datos de salud. Un sitio de marketing normal anota “No es un sitio de salud”. Solo HTTPS no es HIPAA.',
    },
  },
  {
    id: 'pii',
    pattern: /\bPII\b/g,
    label: { en: 'PII', es: 'PII' },
    help: {
      en: 'Personally identifiable information: a name, email, phone, or other detail that points to a person. Forms must not put it in the web address.',
      es: 'Información de identificación personal: un nombre, correo, teléfono u otro dato que señala a una persona. Los formularios no deben ponerlo en la dirección web.',
    },
  },
  {
    id: 'seo',
    pattern: /\bSEO\b/g,
    label: { en: 'SEO', es: 'SEO' },
    help: {
      en: 'Search engine optimization: the basics that help a search engine read the site — a title, one main heading, a short description, a sitemap, and a robots file. It is not a ranking promise.',
      es: 'Optimización para buscadores: lo básico para que un buscador lea el sitio — un título, un encabezado principal, una descripción corta, un sitemap y un archivo robots. No es una promesa de posición.',
    },
  },
  {
    id: 'ada',
    pattern: /\bADA\b/g,
    label: { en: 'ADA', es: 'ADA' },
    help: {
      en: 'Americans with Disabilities Act: the U.S. civil-rights law. Courts often use WCAG as the yardstick for whether a public website is accessible.',
      es: 'Ley de Estadounidenses con Discapacidades: la ley de derechos civiles de EE. UU. Los tribunales suelen usar WCAG como medida de si un sitio público es accesible.',
    },
  },
  {
    id: 'coppa',
    pattern: /\bCOPPA\b/g,
    label: { en: 'COPPA', es: 'COPPA' },
    help: {
      en: 'Children’s Online Privacy Protection Act: extra U.S. rules if the site is directed at children under 13, or if you actually collect their data. Parental consent is required before that collection.',
      es: 'Ley de Protección de la Privacidad Infantil en Línea: reglas extra de EE. UU. si el sitio está dirigido a menores de 13 años o si usted recoge sus datos. Se necesita consentimiento de los padres.',
    },
  },
  {
    id: 'ferpa',
    pattern: /\bFERPA\b/g,
    label: { en: 'FERPA', es: 'FERPA' },
    help: {
      en: 'Family Educational Rights and Privacy Act: U.S. rules that protect student education records. Campus systems that hold those records need named owners, access limits, and a complaint path.',
      es: 'Ley de Derechos Educativos y Privacidad de la Familia: reglas de EE. UU. que protegen los expedientes estudiantiles. Los sistemas del campus que los guardan necesitan dueños, límites de acceso y una vía de queja.',
    },
  },
  {
    id: 'hecvat',
    pattern: /\bHECVAT\b/g,
    label: { en: 'HECVAT', es: 'HECVAT' },
    help: {
      en: 'Higher Education Community Vendor Assessment Toolkit: EDUCAUSE’s questionnaire for campus vendors. NextCompli links to it; it does not copy the licensed questions.',
      es: 'Kit de evaluación de proveedores de educación superior: el cuestionario de EDUCAUSE para vendedores del campus. NextCompli enlaza a él; no copia las preguntas con licencia.',
    },
  },
  {
    id: 'glba',
    pattern: /\bGLBA\b/g,
    label: { en: 'GLBA', es: 'GLBA' },
    help: {
      en: 'Gramm-Leach-Bliley Act: U.S. financial-privacy rules. On campus they apply to student-aid and other financial data, not to every education record.',
      es: 'Ley Gramm-Leach-Bliley: reglas de privacidad financiera de EE. UU. En el campus aplican a ayuda estudiantil y otros datos financieros, no a todo expediente educativo.',
    },
  },
  {
    id: 'pci',
    pattern: /\bPCI(?:\s*DSS)?\b/g,
    label: { en: 'PCI DSS', es: 'PCI DSS' },
    help: {
      en: 'Payment Card Industry Data Security Standard: the card-brand rules if you take credit cards on the site. Scope is the systems that store, process, or transmit card data.',
      es: 'Estándar de Seguridad de Datos de la Industria de Tarjetas de Pago: las reglas de las marcas si cobra con tarjeta en el sitio. El alcance son los sistemas que guardan, procesan o transmiten esos datos.',
    },
  },
  {
    id: 'canspam',
    pattern: /\bCAN-SPAM\b/g,
    label: { en: 'CAN-SPAM', es: 'CAN-SPAM' },
    help: {
      en: 'U.S. commercial-email law: honest subject lines, a real postal address, and a working unsubscribe. It is separate from SPF/DKIM/DMARC, which prove the mail actually came from you.',
      es: 'Ley de correo comercial de EE. UU.: asuntos honestos, dirección postal real y baja que funcione. Es distinta de SPF/DKIM/DMARC, que prueban que el correo realmente salió de usted.',
    },
  },
  {
    id: 'gpc',
    pattern: /\bGPC\b/g,
    label: { en: 'GPC', es: 'GPC' },
    help: {
      en: 'Global Privacy Control: a browser signal that means “do not sell or share my data.” California expects sites to honor it the same way as a Do Not Sell click.',
      es: 'Control Global de Privacidad: una señal del navegador que significa “no venda ni comparta mis datos.” California espera que el sitio la respete igual que un clic de No vender.',
    },
  },
  {
    id: 'cmp',
    pattern: /\bCMPs?\b/g,
    label: { en: 'CMP', es: 'CMP' },
    help: {
      en: 'Consent Management Platform: the cookie banner and log (Cookiebot, OneTrust, and similar). It should block non-essential scripts until the visitor chooses, and keep a record of that choice.',
      es: 'Plataforma de gestión de consentimiento: el banner y el registro de cookies (Cookiebot, OneTrust y similares). Debe bloquear scripts no esenciales hasta que el visitante elija, y guardar esa elección.',
    },
  },
  {
    id: 'csp',
    pattern: /\bCSP\b/g,
    label: { en: 'CSP', es: 'CSP' },
    help: {
      en: 'Content Security Policy: a header that lists which scripts and frames the browser may load, so a pasted attacker script has a harder time running.',
      es: 'Política de seguridad de contenido: un encabezado que lista qué scripts y marcos puede cargar el navegador, para que un script pegado por un atacante tenga más difícil ejecutarse.',
    },
  },
  {
    id: 'hsts',
    pattern: /\bHSTS\b/g,
    label: { en: 'HSTS', es: 'HSTS' },
    help: {
      en: 'HTTP Strict Transport Security: a header that tells browsers to use HTTPS only for this site, so a later visit cannot be downgraded to plain HTTP.',
      es: 'Seguridad de transporte estricta HTTP: un encabezado que dice al navegador usar solo HTTPS en este sitio, para que una visita posterior no baje a HTTP plano.',
    },
  },
  {
    id: 'https',
    pattern: /\bHTTPS\b/g,
    label: { en: 'HTTPS', es: 'HTTPS' },
    help: {
      en: 'Encrypted web traffic (HTTP over TLS). The NextCompli Monitor Agent checks in to the gateway this way today — same idea as a browser using a lock icon. Plain HTTP is not supported.',
      es: 'Tráfico web cifrado (HTTP sobre TLS). Hoy el Agente Monitor de NextCompli reporta al gateway así — como el candado del navegador. HTTP plano no está soportado.',
    },
  },
  {
    id: 'mdm',
    pattern: /\bMDM\b/g,
    label: { en: 'MDM', es: 'MDM' },
    help: {
      en: 'Mobile device management: company software (Intune, Jamf, and similar) that installs and updates apps on many computers at once. You can install the agent by hand or let your MDM push it.',
      es: 'Administración de dispositivos móviles: software de la empresa (Intune, Jamf y similares) que instala y actualiza apps en muchas computadoras a la vez. Puede instalar el agente a mano o dejar que su MDM lo envíe.',
    },
  },
  {
    id: 'gatekeeper',
    pattern: /\bGatekeeper\b/g,
    label: { en: 'Gatekeeper', es: 'Gatekeeper' },
    help: {
      en: 'Apple’s Mac safety check. It blocks apps that are not signed and notarized by Apple. NextCompli’s lab Mac agent is still unsigned, so Gatekeeper shows Not Opened until you approve it once, or until we ship a notarized build.',
      es: 'La comprobación de seguridad de Apple en Mac. Bloquea apps que no están firmadas y notarizadas por Apple. El agente Mac de laboratorio de NextCompli aún no está firmado, así que Gatekeeper muestra No se abrió hasta que lo apruebe una vez, o hasta que enviemos una versión notarizada.',
    },
  },
  {
    id: 'clery',
    pattern: /\bClery\b/g,
    label: { en: 'Clery', es: 'Clery' },
    help: {
      en: 'Jeanne Clery Act: the campus safety law. It requires an annual security report, a public crime log, and timely warnings. This console names the owners. It does not store the crime log.',
      es: 'Ley Jeanne Clery: la ley de seguridad del campus. Exige un informe anual de seguridad, un registro público de delitos y avisos oportunos. Esta consola nombra a los dueños. No guarda el registro de delitos.',
    },
  },
  {
    id: 'titleIx',
    pattern: /\bTitle IX\b/g,
    label: { en: 'Title IX', es: 'Título IX' },
    help: {
      en: 'Title IX: the U.S. rule against sex discrimination in education programs that receive federal funds. This console names the coordinator and the policy. It does not run investigations.',
      es: 'Título IX: la regla de EE. UU. contra la discriminación por sexo en programas educativos que reciben fondos federales. Esta consola nombra al coordinador y la política. No lleva investigaciones.',
    },
  },
  {
    id: 'ipeds',
    pattern: /\bIPEDS\b/g,
    label: { en: 'IPEDS', es: 'IPEDS' },
    help: {
      en: 'Integrated Postsecondary Education Data System: the federal survey of enrollment, prices, and completions that most colleges must file.',
      es: 'Sistema integrado de datos de educación postsecundaria: la encuesta federal de matrícula, precios y titulaciones que la mayoría de las universidades debe presentar.',
    },
  },
  {
    id: 'vawa',
    pattern: /\bVAWA\b/g,
    label: { en: 'VAWA', es: 'VAWA' },
    help: {
      en: 'Violence Against Women Act: campus additions to the Clery report for dating violence, domestic violence, sexual assault, and stalking, plus prevention training.',
      es: 'Ley de Violencia contra la Mujer: añadidos del campus al informe Clery sobre violencia en el noviazgo, violencia doméstica, agresión sexual y acecho, más capacitación de prevención.',
    },
  },
  {
    id: 'irb',
    pattern: /\bIRB\b/g,
    label: { en: 'IRB', es: 'IRB' },
    help: {
      en: 'Institutional Review Board: the campus committee that reviews research with people. This console names the owner. It does not store participant records.',
      es: 'Junta de revisión institucional: el comité del campus que revisa la investigación con personas. Esta consola nombra al dueño. No guarda expedientes de participantes.',
    },
  },
  {
    id: 'hea',
    pattern: /\bHEA\b/g,
    label: { en: 'HEA', es: 'HEA' },
    help: {
      en: 'Higher Education Act: the federal law behind student aid, consumer information, and foreign-gift reporting for colleges.',
      es: 'Ley de Educación Superior: la ley federal detrás de la ayuda estudiantil, la información al consumidor y el reporte de regalos extranjeros para las universidades.',
    },
  },
  {
    id: 'erisa',
    pattern: /\bERISA\b/g,
    label: { en: 'ERISA', es: 'ERISA' },
    help: {
      en: 'Employee Retirement Income Security Act. The federal law for many employer retirement and life-insurance plans. The person named on the plan form is who gets paid. A will does not replace that form.',
      es: 'Ley de seguridad de los ingresos de jubilación de los empleados. La ley federal de muchos planes de jubilación y de seguro de vida del empleador. Cobra la persona nombrada en el formulario del plan. Un testamento no sustituye ese formulario.',
    },
  },
  {
    id: 'sns',
    pattern: /\bSNS\b/g,
    label: { en: 'SNS', es: 'SNS' },
    help: {
      en: 'Amazon Simple Notification Service. The Amazon service that sends the Postlude text message.',
      es: 'Servicio simple de notificaciones de Amazon. El servicio de Amazon que envía el mensaje de texto de Postlude.',
    },
  },
];

const findNextTerm = (
  text: string,
  from: number,
): { start: number; end: number; id: DodGlossaryTermId } | null => {
  let best: { start: number; end: number; id: DodGlossaryTermId } | null = null;
  for (const term of DOD_GLOSSARY) {
    const flags = term.pattern.flags.replace('g', '');
    const re = new RegExp(term.pattern.source, flags.includes('i') ? `${flags}g` : `${flags}g`);
    re.lastIndex = from;
    const match = re.exec(text);
    if (!match || match.index < from) continue;
    const end = match.index + match[0].length;
    if (
      !best
      || match.index < best.start
      || (match.index === best.start && end - match.index > best.end - best.start)
    ) {
      best = { start: match.index, end, id: term.id };
    }
  }
  return best;
};

export const annotateDodTerms = (
  text: string,
  seen: Set<DodGlossaryTermId> = new Set(),
): DodGlossaryChunk[] => {
  const chunks: DodGlossaryChunk[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const next = findNextTerm(text, cursor);
    if (!next) {
      chunks.push({ text: text.slice(cursor) });
      break;
    }
    if (next.start > cursor) {
      chunks.push({ text: text.slice(cursor, next.start) });
    }
    const word = text.slice(next.start, next.end);
    if (seen.has(next.id)) {
      chunks.push({ text: word });
    } else {
      seen.add(next.id);
      chunks.push({ text: word, termId: next.id });
    }
    cursor = next.end;
  }
  return chunks.filter((chunk) => chunk.text.length > 0);
};

export const dodGlossaryTerm = (id: DodGlossaryTermId): DodGlossaryTerm => {
  const term = DOD_GLOSSARY.find((row) => row.id === id);
  if (!term) {
    throw new Error(`Unknown DoD glossary term: ${id}`);
  }
  return term;
};
